/**
 * Danger Zone Page
 *
 * Account deactivation and deletion with confirmation safeguards.
 * Uses changeUserStatus (INACTIVE) and deleteUser mutations.
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  useToast,
  ConfirmPanel,
  Badge,
} from '@bloomcode/ui';
import { MetaTags } from '@bloomcode/shared-modules';
import { useCookieSession } from '@bloomcode/auth';
import { useDeleteUserMutation, useChangeUserStatusMutation, UserStatus } from '@bloomcode/graphql';
import { AlertTriangle, Trash2, PauseCircle, ShieldAlert } from '@bloomcode/icons';

export default function DangerZonePage() {
  const { session, logout } = useCookieSession();
  const { toast } = useToast();

  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [changeUserStatus, { loading: deactivating }] = useChangeUserStatusMutation();
  const [deleteUser, { loading: deleting }] = useDeleteUserMutation();

  const handleDeactivate = async () => {
    if (!session?.userId) return;
    try {
      const result = await changeUserStatus({
        variables: {
          input: {
            targetUserId: session.userId,
            status: UserStatus.INACTIVE,
          },
        },
      });
      if (result.data?.changeUserStatus?.success) {
        toast({
          title: 'Account deactivated',
          description: 'Your account has been deactivated. You will be signed out.',
        });
        setShowDeactivate(false);
        setTimeout(() => logout(), 2000);
      } else {
        toast({
          title: 'Failed to deactivate',
          description: result.data?.changeUserStatus?.message || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!session?.userId || deleteConfirmText !== 'DELETE') return;
    try {
      const result = await deleteUser({
        variables: { targetUserId: session.userId },
      });
      if (result.data?.deleteUser?.success) {
        toast({
          title: 'Account deleted',
          description: 'Your account has been permanently deleted. You will be signed out.',
        });
        setShowDelete(false);
        setTimeout(() => logout(), 2000);
      } else {
        toast({
          title: 'Failed to delete account',
          description: result.data?.deleteUser?.message || 'Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Danger Zone - Bloomcode Account</title>
      </Helmet>
      <MetaTags title="Danger Zone" description="Account deactivation and deletion" />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Danger Zone</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="text-2xl font-bold">Danger Zone</h1>
          <p className="text-muted-foreground mt-1">Irreversible actions for your account</p>
        </div>

        <div className="space-y-6">
          {/* Deactivate Account */}
          <Card className="border-primary/30">
            <CardHeader>
              <div className="flex items-center gap-2">
                <PauseCircle className="h-5 w-5 text-primary" />
                <CardTitle>Deactivate Account</CardTitle>
              </div>
              <CardDescription>Temporarily disable your account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-foreground">What happens when you deactivate:</p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                      <li>Your account will be hidden from other users</li>
                      <li>You won&apos;t be able to sign in until reactivated</li>
                      <li>Your data will be preserved</li>
                      <li>Contact support to reactivate your account</li>
                    </ul>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={() => setShowDeactivate(true)}>
                <PauseCircle className="h-4 w-4 mr-2" />
                Deactivate Account
              </Button>
            </CardContent>
          </Card>

          {/* Delete Account */}
          <Card className="border-destructive/30">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-destructive" />
                <CardTitle>Delete Account</CardTitle>
                <Badge variant="destructive">Permanent</Badge>
              </div>
              <CardDescription>
                Permanently delete your account and all associated data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-foreground">
                      This action is irreversible. Deleting your account will:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                      <li>Permanently remove all your personal data</li>
                      <li>Cancel all active subscriptions</li>
                      <li>Remove access to all organizations and teams</li>
                      <li>Delete all sessions and authentication data</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="max-w-sm space-y-2">
                <Label htmlFor="deleteConfirm" className="text-sm">
                  Type <span className="font-mono font-bold text-destructive">DELETE</span> to
                  confirm
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                />
              </div>

              <Button
                variant="destructive"
                onClick={() => setShowDelete(true)}
                disabled={deleteConfirmText !== 'DELETE'}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account Permanently
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmPanel
        open={showDeactivate}
        onConfirm={handleDeactivate}
        onCancel={() => setShowDeactivate(false)}
        title="Deactivate Your Account?"
        description="Your account will be temporarily disabled. You can contact support to reactivate it later."
        confirmText={deactivating ? 'Deactivating...' : 'Deactivate'}
        confirmVariant="destructive"
      />

      <ConfirmPanel
        open={showDelete}
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDelete(false);
          setDeleteConfirmText('');
        }}
        title="Permanently Delete Your Account?"
        description="This action cannot be undone. All your data, subscriptions, and access will be permanently removed."
        confirmText={deleting ? 'Deleting...' : 'Delete Forever'}
        confirmVariant="destructive"
      />
    </>
  );
}
